import React, { Fragment } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useDialogs } from '../stores/dialogStore';
import { Button } from './Button';
import { Text } from './Text';

// Constants for dialog styling
const SIZE_CLASSES = {
  sm: 'max-w-md',
  md: 'max-w-lg', 
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-7xl'
} as const;

const TYPE_CONFIG = {
  info: {
    icon: InformationCircleIcon,
    iconColor: 'text-blue-600',
    buttonColor: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
  },
  success: {
    icon: CheckCircleIcon,
    iconColor: 'text-green-600',
    buttonColor: 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
  },
  warning: {
    icon: ExclamationTriangleIcon,
    iconColor: 'text-black',
    buttonColor: 'bg-black hover:bg-gray-500 focus:ring-yellow-500'
  },
  danger: {
    icon: XCircleIcon,
    iconColor: 'text-red-600',
    buttonColor: 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
  }
} as const;

const ICON_SIZE = "h-6 w-6";
const CLOSE_ICON_SIZE = "h-5 w-5";
const BACKDROP_CLASSES = "fixed inset-0 bg-gray-200/25 backdrop-blur-xs";
const TRANSITION_DURATION = {
  enter: "ease-out duration-300",
  leave: "ease-in duration-200"
};

type DialogType = keyof typeof TYPE_CONFIG;
type DialogSize = keyof typeof SIZE_CLASSES;

interface DialogData {
  id: string;
  type?: DialogType;
  size?: DialogSize;
  title?: string;
  content: React.ReactNode | string;
  closable?: boolean;
  showCloseButton?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
}

// Helper function to get dialog configuration
const getDialogConfig = (type?: DialogType) => {
  return type ? TYPE_CONFIG[type] : TYPE_CONFIG.info;
};

// Helper function to handle dialog close
const handleDialogClose = (dialog: DialogData, close: (id: string) => void) => {
  if (dialog.closable !== false) {
    if (dialog.onCancel) {
      dialog.onCancel();
    } else {
      close(dialog.id);
    }
  }
};

// Dialog Header Component
const DialogHeader = ({ dialog, config, onClose }: {
  dialog: DialogData;
  config: typeof TYPE_CONFIG[DialogType];
  onClose: () => void;
}) => {
  if (!dialog.title && !dialog.showCloseButton) return null;

  const IconComponent = config.icon;

  return (
    <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0">
      {dialog.title && (
        <DialogTitle className="flex items-center gap-3">
          {dialog.type && (
            <IconComponent className={clsx(ICON_SIZE, config.iconColor)} />
          )}
          <Text variant="subtitle" weight="medium" color="primary">
            {dialog.title}
          </Text>
        </DialogTitle>
      )}
      {dialog.showCloseButton && (
        <Button onClick={onClose} variant="secondary" size="sm">
          <XMarkIcon className={CLOSE_ICON_SIZE} aria-hidden="true" />
        </Button>
      )}
    </div>
  );
};

// Dialog Content Component
const DialogContent = ({ dialog }: { dialog: DialogData }) => {
  const contentClasses = clsx(
    "text-sm text-text-secondary flex-1 overflow-y-auto",
    "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400",
    (dialog.title || dialog.showCloseButton) ? 'px-6' : 'p-6',
    (dialog.onConfirm || dialog.onCancel) ? 'pb-4' : 'pb-6'
  );

  return (
    <div className={contentClasses}>
      {typeof dialog.content === 'string' ? (
        <Text variant="body" color="secondary">
          {dialog.content}
        </Text>
      ) : (
        dialog.content
      )}
    </div>
  );
};

// Dialog Footer Component
const DialogFooter = ({ dialog, config }: {
  dialog: DialogData;
  config: typeof TYPE_CONFIG[DialogType];
}) => {
  if (!dialog.onConfirm && !dialog.onCancel) return null;

  return (
    <div className="flex items-center justify-end gap-3 px-6">
      {dialog.onCancel && (
        <Button
          variant="secondary"
          onClick={dialog.onCancel}
        >
          {dialog.cancelText || 'Cancel'}
        </Button>
      )}
      {dialog.onConfirm && (
        <button
          type="button"
          className={clsx(
            "px-4 py-2 text-sm font-medium text-white rounded-md",
            "focus:outline-none focus:ring-2 focus:ring-offset-2",
            config.buttonColor
          )}
          onClick={dialog.onConfirm}
        >
          {dialog.confirmText || 'Confirm'}
        </button>
      )}
    </div>
  );
};

// Individual Dialog Component
const DialogItem = ({ dialog }: { dialog: DialogData }) => {
  const { close } = useDialogs();
  const config = getDialogConfig(dialog.type);
  
  const handleClose = () => handleDialogClose(dialog, close);
  
  const panelClasses = clsx(
    "bg-white w-full max-h-[90vh] transform overflow-hidden rounded-md",
    "text-left align-middle shadow-sm transition-all flex flex-col p-0 pb-4",
    SIZE_CLASSES[dialog.size || 'md']
  );

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-50" 
        onClose={handleClose}
      >
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter={TRANSITION_DURATION.enter}
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave={TRANSITION_DURATION.leave}
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={BACKDROP_CLASSES} />
        </TransitionChild>

        {/* Dialog container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter={TRANSITION_DURATION.enter}
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave={TRANSITION_DURATION.leave}
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className={panelClasses}>
                <DialogHeader dialog={dialog} config={config} onClose={handleClose} />
                <DialogContent dialog={dialog} />
                <DialogFooter dialog={dialog} config={config} />
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export const DialogContainer = () => {
  const { dialogs } = useDialogs();

  return (
    <>
      {dialogs.map((dialog) => (
        <DialogItem key={dialog.id} dialog={dialog} />
      ))}
    </>
  );
};